// src/pages/AllGames.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAllGames } from "../hooks/useAllGames";
import AllGameCard from "../components/AllGameCard";
import GamesLoader from "../components/GamesLoader";

const PAGE_SIZE = 100;

export default function AllGames() {
  const navigate = useNavigate();
  const { games, loading } = useAllGames();
  const [uiLoading, setUiLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");
  const [tag, setTag] = useState("All");
  const [web3Only, setWeb3Only] = useState(false);
  const [web2Only, setWeb2Only] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const listRef = useRef(null);

  const genres = useMemo(() => {
    const set = new Set();
    games.forEach((g) => g.genre && set.add(g.genre));
    return ["All", ...Array.from(set).sort()];
  }, [games]);

  const tags = useMemo(() => {
    const set = new Set();
    games.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return ["All", ...Array.from(set).sort()];
  }, [games]);

  const results = useMemo(() => {
    let list = [...games];

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => {
        const haystack = [
          g.title,
          g.description,
          g.genre,
          ...(g.tags || []),
          g.source,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (genre !== "All") list = list.filter((g) => g.genre === genre);
    if (tag !== "All") list = list.filter((g) => (g.tags || []).includes(tag));

    if (web3Only) list = list.filter((g) => g.isWeb3);
    if (web2Only) list = list.filter((g) => !g.isWeb3);

    return list;
  }, [games, query, genre, tag, web3Only, web2Only]);

  // 🔁 Reset how many are visible when filters/search change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, genre, tag, web3Only, web2Only]);

  const visibleResults = useMemo(
    () => results.slice(0, visibleCount),
    [results, visibleCount]
  );

  const canShowMore = visibleCount < results.length;

useEffect(() => {
  // If data is still loading from the hook, keep UI loader on
  if (loading) {
    setUiLoading(true);
    return;
  }

  // When results change (new filters/search/etc), show loader briefly
  setUiLoading(true);
  const t = setTimeout(() => {
    setUiLoading(false);
  }, 200); // 200ms feels snappy; you can tweak

  return () => clearTimeout(t);
}, [loading, results.length]);


  return (
    <div ref={listRef} className="grid gap-6 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
      
      <h1 className="text-3xl font-bold tracking-tight">All Games</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 shadow-sm shadow-emerald-500/10">
        {/* Search */}
        <input
          placeholder="Search games…"
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 min-w-[240px] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {/* Genre */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          {genres.map((g) => (
            <option key={g} value={g}>
              {g === "All" ? "All genres" : g}
            </option>
          ))}
        </select>

        {/* Tag */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {tags.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "All tags" : t}
            </option>
          ))}
        </select>

        {/* Web3 toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb3Only(!web3Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web3Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web3Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web3 only</span>
        </div>

        {/* Web2 toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb2Only(!web2Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web2Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web2Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web2 only</span>
        </div>
      </div>

      {/* Results */}
      {uiLoading  ? (
         <GamesLoader />
      ) : (
        <>
          <div className="space-y-3">
            {visibleResults.map((game) => (
              <AllGameCard
                key={game.id}
                game={game}
                onOpen={() => navigate(`/game/${game.id}`)}
                onPlay={() => navigate(`/play/${game.id}`)}
              />
            ))}
          </div>

         {canShowMore && (
  <div className="mt-4 flex justify-center gap-3">
    
    {/* Show More */}
    <button
      type="button"
      onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
      className="px-4 py-2 text-sm rounded-xl border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"
    >
      Show more games ({results.length - visibleCount} remaining)
    </button>

 {/* Back to Top */}
<button
  type="button"
  onClick={() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }}
  className="px-4 py-2 text-sm rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-600/20"
>
  Back to Top ↑
</button>
  </div>
)}


          <div className="spacer" style={{ height: 100 }} />
        </>
      )}
    </div>
  );
}
